// @wc-ignore-file
import type {
  FigmaContextImportRequest,
  FigmaContextRefreshRequest,
} from "@shared/types/figma-context";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import {
  type FigmaContextError,
  type FigmaImportRow,
  getFigmaImportByDocument,
  getFigmaImportByTarget,
  importFigmaContext,
  refreshFigmaContext,
} from "../api/figma-api";

/** A row another session is refreshing is re-read until it settles (CR-126 review). */
export const FIGMA_REFRESHING_POLL_MS = 5_000;

export function figmaRowRefetchInterval(row: FigmaImportRow | null | undefined): number | false {
  return row?.status === "refreshing" ? FIGMA_REFRESHING_POLL_MS : false;
}

export const figmaQueries = {
  all: () => ["figma-import"] as const,
  project: (projectId: string) => [...figmaQueries.all(), projectId] as const,
  byTarget: (projectId: string, fileKey: string, nodeId: string | null) =>
    queryOptions({
      queryKey: [...figmaQueries.project(projectId), fileKey, nodeId] as const,
      queryFn: () => getFigmaImportByTarget(projectId, fileKey, nodeId),
      refetchInterval: (query) => figmaRowRefetchInterval(query.state.data),
    }),
  byDocument: (projectId: string, documentId: string) =>
    queryOptions({
      queryKey: [...figmaQueries.project(projectId), "document", documentId] as const,
      queryFn: () => getFigmaImportByDocument(projectId, documentId),
      refetchInterval: (query) => figmaRowRefetchInterval(query.state.data),
    }),
};

export function useFigmaImportQuery(projectId: string, fileKey: string, nodeId: string | null) {
  return useQuery(figmaQueries.byTarget(projectId, fileKey, nodeId));
}

export function useFigmaImportByDocumentQuery(projectId: string, documentId: string) {
  return useQuery(figmaQueries.byDocument(projectId, documentId));
}

export function useImportFigmaContextMutation(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    Awaited<ReturnType<typeof importFigmaContext>>,
    FigmaContextError,
    Omit<FigmaContextImportRequest, "containerId">
  >({
    mutationFn: (request) => importFigmaContext({ ...request, containerId: projectId }),
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: figmaQueries.project(projectId),
      }),
  });
}

export function useRefreshFigmaContextMutation(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    Awaited<ReturnType<typeof refreshFigmaContext>>,
    FigmaContextError,
    Omit<FigmaContextRefreshRequest, "containerId">
  >({
    mutationFn: (request) => refreshFigmaContext({ ...request, containerId: projectId }),
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: figmaQueries.project(projectId),
      }),
  });
}

/** D4: a view refreshes an import at most once per window. */
export const FIGMA_VIEW_REFRESH_WINDOW_MS = 10 * 60 * 1000;

/**
 * Session-level memory of the view refreshes already asked for, shared by every
 * card and banner: the same target shown under five messages refreshes once.
 * The server throttles too; this only saves the round trips.
 */
const viewRefreshAskedAt = new Map<string, number>();

/** Test-only: forget the session's view refreshes. */
export function resetFigmaViewRefreshes(): void {
  viewRefreshAskedAt.clear();
}

/** Whether showing `row` now should ask for a view refresh (and records it). */
export function claimFigmaViewRefresh(row: FigmaImportRow, now: number = Date.now()): boolean {
  if (row.status === "refreshing") return false;
  const lastSuccess = row.last_successful_refresh_at
    ? new Date(row.last_successful_refresh_at).getTime()
    : Number.NaN;
  if (Number.isFinite(lastSuccess) && now - lastSuccess < FIGMA_VIEW_REFRESH_WINDOW_MS) {
    return false;
  }
  const asked = viewRefreshAskedAt.get(row.id);
  if (asked !== undefined && now - asked < FIGMA_VIEW_REFRESH_WINDOW_MS) return false;
  viewRefreshAskedAt.set(row.id, now);
  return true;
}

/** Refresh-on-view (D4): asks once per window when the import is shown. */
export function useFigmaViewRefresh(
  row: FigmaImportRow | null | undefined,
  refresh: (request: Omit<FigmaContextRefreshRequest, "containerId">) => void,
): void {
  // Keyed on the row's identity and refresh state, not on the object or the
  // callback (both change identity on every render).
  const latest = useRef({ row, refresh });
  latest.current = { row, refresh };
  const importId = row?.id;
  const lastSuccess = row?.last_successful_refresh_at;
  const status = row?.status;
  useEffect(() => {
    const { row: current, refresh: ask } = latest.current;
    if (!current) return;
    if (claimFigmaViewRefresh(current)) ask({ importId: current.id, reason: "view" });
    // importId/lastSuccess/status are the trigger; the values come from `latest`.
  }, [importId, lastSuccess, status]);
}
